import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useLiveSource } from "@/hooks/useLiveSource";
import { toast } from "sonner";
import {
  Activity, Search, Filter, RefreshCw, Trash2, Settings, ChevronLeft,
  ChevronRight, Clock, Globe, User, AlertCircle, CheckCircle2, Copy,
} from "lucide-react";

const PROVIDERS = ["manarecharge", "msg91", "razorpay", "cashfree", "runpaisa"];
const ROLES = ["admin", "partner", "system", "approver_l1", "approver_l2", "approver_l3", "approver_all"];

export default function AdminApiLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 30;

  // Filters
  const [filterProvider, setFilterProvider] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterSuccess, setFilterSuccess] = useState("");
  const [filterSearch, setFilterSearch] = useState("");

  // Detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Retention config
  const [showConfig, setShowConfig] = useState(false);
  const [retention, setRetention] = useState({});
  const [retentionLoading, setRetentionLoading] = useState(false);
  const { source } = useLiveSource();

  const fetchLogs = async (off = 0) => {
    setLoading(true);
    try {
      if (source === "live") {
        const filters = {};
        if (filterProvider) filters.provider = filterProvider;
        if (filterRole) filters.user_role = filterRole;
        if (filterSuccess) filters.success = filterSuccess === "true";
        const { data } = await api.post("/admin/system-test/live-db", {
          table: "external_api_logs",
          filters,
          sort_field: "created_at",
          sort_dir: -1,
          limit: limit + off,    // db-read doesn't paginate; over-fetch then slice
        });
        const rows = (data.rows || []).slice(off, off + limit);
        setLogs(rows);
        setTotal(data.rows?.length || 0);
        setOffset(off);
        return;
      }
      const params = new URLSearchParams({ limit: String(limit), offset: String(off) });
      if (filterProvider) params.set("provider", filterProvider);
      if (filterRole) params.set("user_role", filterRole);
      if (filterSuccess) params.set("success", filterSuccess);
      if (filterSearch) params.set("search", filterSearch);
      const { data } = await api.get(`/admin/api-logs?${params}`);
      setLogs(data.items || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(0); }, [filterProvider, filterRole, filterSuccess, source]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/api-logs/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error("Failed to load log detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const loadRetention = async () => {
    try {
      const { data } = await api.get("/admin/api-logs/config/retention");
      setRetention(data.retention_days || {});
    } catch { /* ignore */ }
  };

  const saveRetention = async () => {
    setRetentionLoading(true);
    try {
      await api.patch("/admin/api-logs/config/retention", { retention_days: retention });
      toast.success("Retention config saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setRetentionLoading(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm("Delete logs older than configured retention? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/api-logs/cleanup");
      toast.success(`Cleanup complete · ${data.deleted} logs deleted`);
      fetchLogs(0);
    } catch (err) {
      toast.error("Cleanup failed");
    }
  };

  const statusBadge = (success, status) => {
    if (success) return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{status || "OK"}</Badge>;
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">{status || "ERR"}</Badge>;
  };

  const providerColor = (p) => {
    const m = { manarecharge: "bg-blue-100 text-blue-700", msg91: "bg-purple-100 text-purple-700", razorpay: "bg-indigo-100 text-indigo-700", cashfree: "bg-green-100 text-green-700", runpaisa: "bg-orange-100 text-orange-700" };
    return m[p] || "bg-gray-100 text-gray-700";
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="admin-api-logs">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">External API Logs</h1>
            <p className="text-sm text-muted-foreground mt-1">Every outbound HTTP call to providers — request, response, timing, user context.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { loadRetention(); setShowConfig(true); }} data-testid="api-logs-config">
              <Settings className="h-4 w-4 mr-1" /> Config
            </Button>
            <Button variant="outline" size="sm" onClick={runCleanup} data-testid="api-logs-cleanup">
              <Trash2 className="h-4 w-4 mr-1" /> Cleanup
            </Button>
            <Button size="sm" onClick={() => fetchLogs(0)} data-testid="api-logs-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end" data-testid="api-logs-filters">
          <div>
            <Label className="text-xs">Provider</Label>
            <select className="h-8 rounded-md border bg-transparent px-2 text-sm w-36" value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}>
              <option value="">All providers</option>
              {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">User role</Label>
            <select className="h-8 rounded-md border bg-transparent px-2 text-sm w-36" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select className="h-8 rounded-md border bg-transparent px-2 text-sm w-28" value={filterSuccess} onChange={(e) => setFilterSuccess(e.target.value)}>
              <option value="">All</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search URL</Label>
            <div className="flex gap-1">
              <Input className="h-8 text-sm" placeholder="Search by URL..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchLogs(0)} data-testid="api-logs-search" />
              <Button size="sm" variant="outline" className="h-8" onClick={() => fetchLogs(0)}><Search className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Time</th>
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className="text-left px-3 py-2 font-medium">Action</th>
                  <th className="text-left px-3 py-2 font-medium">Method</th>
                  <th className="text-left px-3 py-2 font-medium">URL</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Time (ms)</th>
                  <th className="text-left px-3 py-2 font-medium">User</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>}
                {!loading && logs.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No logs yet. Logs appear when external API calls are made.</td></tr>}
                {logs.map((log) => (
                  <tr key={log.id} className="border-b hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => openDetail(log.id)} data-testid={`api-log-row-${log.id}`}>
                    <td className="px-3 py-2 text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${providerColor(log.provider)}`}>{log.provider}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{log.action || "—"}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{log.request_method}</Badge></td>
                    <td className="px-3 py-2 text-xs font-mono max-w-[300px] truncate">{log.request_url}</td>
                    <td className="px-3 py-2">{statusBadge(log.success, log.response_status)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{log.elapsed_ms}ms</td>
                    <td className="px-3 py-2 text-xs">{log.user_email || <span className="text-muted-foreground">system</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => fetchLogs(Math.max(0, offset - limit))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button size="sm" variant="outline" disabled={offset + limit >= total} onClick={() => fetchLogs(offset + limit)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {detail?.request_method} {detail?.provider}
            </DialogTitle>
            <DialogDescription>{detail?.request_url}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Provider:</span> <span className="font-medium">{detail.provider}</span></div>
                <div><span className="text-muted-foreground">Action:</span> <span className="font-medium">{detail.action || "—"}</span></div>
                <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{detail.category || "—"}</span></div>
                <div><span className="text-muted-foreground">Elapsed:</span> <span className="font-mono">{detail.elapsed_ms}ms</span></div>
                <div><span className="text-muted-foreground">User:</span> <span className="font-medium">{detail.user_email || "system"}</span></div>
                <div><span className="text-muted-foreground">Role:</span> <Badge variant="outline" className="text-[10px]">{detail.user_role || "—"}</Badge></div>
                <div><span className="text-muted-foreground">Status:</span> {statusBadge(detail.success, detail.response_status)}</div>
                <div><span className="text-muted-foreground">Time:</span> <span className="text-xs font-mono">{detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"}</span></div>
              </div>
              {detail.error && <div className="rounded-md bg-destructive/5 border border-destructive/20 p-3 text-destructive text-xs">{detail.error}</div>}
              <JsonBlock title="Request Headers" data={detail.request_headers} />
              <JsonBlock title="Request Body" data={detail.request_body} />
              <JsonBlock title="Response Headers" data={detail.response_headers} />
              <JsonBlock title="Response Body" data={detail.response_body} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Retention Config Modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Retention Configuration</DialogTitle>
            <DialogDescription>Set how many days to keep logs per provider. Set to 0 to keep forever.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {PROVIDERS.map((p) => (
              <div key={p} className="flex items-center justify-between">
                <Label className="capitalize">{p}</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} className="w-20 h-8 text-sm text-center" value={retention[p] ?? 30}
                    onChange={(e) => setRetention({ ...retention, [p]: parseInt(e.target.value) || 0 })} />
                  <span className="text-xs text-muted-foreground">days</span>
                </div>
              </div>
            ))}
            <Button className="w-full mt-2" onClick={saveRetention} disabled={retentionLoading}>
              {retentionLoading ? "Saving..." : "Save retention config"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function JsonBlock({ title, data }) {
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const text = JSON.stringify(data, null, 2);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-medium">{title}</Label>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          <Copy className="h-3 w-3 mr-1" /> {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="rounded-md bg-muted p-3 text-xs font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">{text}</pre>
    </div>
  );
}
